import { supabase } from "../lib/supabase";
import type {
  SearchParams,
  SortParams,
  FetchResult,
} from "../components/Manager/types";

export type Agency = {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
  territory_count?: number;
};

export async function fetchAgencies(
  limit?: number,
  offset?: number,
  searchParams?: SearchParams,
  sortParams?: SortParams,
): Promise<FetchResult<Agency>> {
  const applyFilters = (q: any): any => {
    let result = q;
    if (searchParams?.text) {
      result = result.ilike("name", `%${searchParams.text}%`);
    }
    return result;
  };

  // Get total count
  let countQuery = supabase
    .from("agencies")
    .select("*", { count: "exact", head: true });
  countQuery = applyFilters(countQuery);
  const { count } = await countQuery;

  // Get data with territory count
  let query = supabase.from("agencies").select(`
    *,
    territories(id)
  `);

  query = applyFilters(query);

  const sortColumn = sortParams?.column ?? "name";
  const sortAscending = sortParams?.order !== "desc";

  if (sortColumn !== "territory_count") {
    query = query.order(sortColumn, { ascending: sortAscending });
  }

  if (limit) {
    query = query.limit(limit + 1);
  }

  if (offset) {
    query = query.range(offset, offset + (limit ?? 10));
  }

  const { error, data } = await query;
  if (error) throw error;

  const items = (data ?? []).map((item: any) => ({
    ...item,
    territory_count: item.territories?.length ?? 0,
    territories: undefined,
  })) as Agency[];

  if (sortColumn === "territory_count") {
    items.sort((a, b) => {
      const diff = (a.territory_count ?? 0) - (b.territory_count ?? 0);
      return sortAscending ? diff : -diff;
    });
  }

  const hasMore = limit ? items.length > limit : false;
  const limitedData = limit ? items.slice(0, limit) : items;

  return { data: limitedData, hasMore, totalCount: count ?? undefined };
}

/**
 * Search agencies by name prefix. Used by autocomplete components.
 */
export async function searchAgencies(
  searchText: string,
  limit = 10,
): Promise<Agency[]> {
  if (!searchText.trim()) {
    return [];
  }

  const { data, error } = await supabase
    .from("agencies")
    .select("*")
    .ilike("name", `%${searchText}%`)
    .order("name", { ascending: true })
    .limit(limit);

  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch a single agency by ID. Used to display the selected agency name.
 */
export async function fetchAgencyById(id: number): Promise<Agency | null> {
  const { data, error } = await supabase
    .from("agencies")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }
  return data;
}

/**
 * Fetch all agencies. Used for dropdown selects.
 */
export async function fetchAllAgencies(): Promise<Agency[]> {
  const { data, error } = await supabase
    .from("agencies")
    .select("*")
    .order("name", { ascending: true });

  if (error) throw error;
  return data ?? [];
}
